<script setup lang="ts">
import { ref } from 'vue'
import { i18n } from '@/i18n'
import useAppStore from '@/composition/appStore'

const appStore = useAppStore()
const t = i18n.global.t

defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  newPassword: [value: string]
}>()

const memorablePassword = ref('')
const loading = ref(false)
const error = ref('')

async function handleLogin() {
  if (!memorablePassword.value.trim()) {
    error.value = t('pleaseEnterMemorablePassword')
    return
  }

  loading.value = true
  error.value = ''

  try {
    await appStore.actions.loginUser(memorablePassword.value.trim())
    emit('update:modelValue', false)
    memorablePassword.value = ''
  } catch (err) {
    error.value = (err as Error).message || t('loginFailed')
  } finally {
    loading.value = false
  }
}

function cancel() {
  emit('update:modelValue', false)
  memorablePassword.value = ''
  error.value = ''
}

async function createNewAccount() {
  const newPassword = await appStore.actions.createUserAccount()
  emit('update:modelValue', false)
  memorablePassword.value = ''
  error.value = ''
  // This will trigger account creation in parent component
  emit('newPassword', newPassword)
}
</script>

<template>
  <div
    v-if="modelValue"
    class="absolute w-full h-full p-20 bg-black/70 z-20 font-alexandria flex flex-col items-center justify-start"
  >
    <div class="bg-white rounded-2xl flex flex-col p-8 max-w-xl">
      <h1 class="text-3xl font-bold">{{ t('loginToAccount') }}</h1>
      <p class="text-md pt-3 text-grey">{{ t('enterMemorablePasswordToLogin') }}</p>

      <!-- User enters memorable password to restore account access -->
      <div class="mt-6">
        <label class="text-sm font-semibold">{{ t('memorablePassword') }}</label>
        <input
          v-model="memorablePassword"
          type="text"
          :placeholder="t('memorablePasswordPlaceholder')"
          class="w-full border rounded-lg border-grey p-3 mt-2"
          @keyup.enter="handleLogin"
          :disabled="loading"
        />
      </div>

      <!-- Displays login validation errors -->
      <div v-if="error" class="mt-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
        {{ error }}
      </div>

      <!-- Format hints with example memorable passwords -->
      <div class="mt-4 p-3 bg-grey/10 rounded-lg">
        <p class="text-sm text-grey">
          <strong>{{ t('example') }}:</strong> happy-elephant, gentle-river, bright-mountain
        </p>
      </div>

      <!-- Primary actions: cancel, create account, or proceed with login -->
      <div class="flex flex-row justify-between pt-6">
        <button
          class="px-4 py-2 rounded-lg border border-grey text-grey cursor-pointer hover:bg-grey/10 transition-colors"
          @click="cancel"
          :disabled="loading"
        >
          {{ t('cancel') }}
        </button>

        <div class="flex gap-3">
          <button
            class="px-4 py-2 rounded-lg border border-green text-green cursor-pointer hover:bg-green/10 transition-colors"
            @click="createNewAccount"
            :disabled="loading"
          >
            {{ t('createNewAccount') }}
          </button>
          <button
            class="px-6 py-2 rounded-lg bg-green text-white cursor-pointer hover:bg-green/80 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            @click="handleLogin"
            :disabled="loading || !memorablePassword.trim()"
          >
            {{ loading ? t('loggingIn') : t('login') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
